import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid,
  Snackbar
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { Header } from '../components-molecules';
import { ThemeProvider } from '@material-ui/core';
import { ITheme, colours, theme } from '../constants/theme';
import axios from 'axios';
import { setError } from '../redux/actions/error';
import ButtonWithLoading from "../components-atoms/ButtonWithLoadingIndicator";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  paper: {
    padding: " 24px",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    boxSizing: "border-box",
    borderRadius: "20px",
    margin: "0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "inherit",
  },
  signIntitle: {
    marginBottom: 12,
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(3),
    alignItems: "center",
  },
  submit: {
    backgroundColor: colours.primary,
    borderRadius: "8px",
    padding: "6px 16px",
  },
  textField: {
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "16px",
    lineHeight: "19px",
    letterSpacing: "0.25px",
    color: "rgba(0, 0, 0, 0.87)",
  },
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  cssFocused: {},
  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
}));

const Alert = (props: any) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const ForgotPassword: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  const dispatch = useDispatch();
  // const error = useSelector((state: any) => state.error.message);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      dispatch(setError('Please enter your email address'));
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post('/portal/forgot-password', { Email: email });
      setSuccess(true);
      setOpenSnackbar(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        dispatch(setError(error.response.data.error));
      } else {
        dispatch(setError('An error occurred. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <Header />
        <Container component="main" maxWidth="xs">
          <Container className={classes.paper}>
            <ThemeProvider theme={theme}>
              <Typography
                component="h6"
                variant="h6"
                className={classes.signIntitle}
              >
                Forgot Password
              </Typography>
              <Typography component="p" variant="body2" color="textSecondary">
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              
              {success ? (
                <Grid container spacing={2} style={{ marginTop: '20px' }}>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      If your email is registered, you will receive a password reset link shortly.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} container justifyContent="center">
                    <ButtonWithLoading
                      onClick={() => window.location.href = '/'}
                      loading={false}
                      title={t('forgotPassword.backToSignIn')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <form className={classes.form} onSubmit={handleSubmit}>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('forgotPassword.emailAddress')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputProps={{
                      classes: {
                        root: classes.cssOutlinedInput,
                        focused: classes.cssFocused,
                        notchedOutline: classes.notchedOutline,
                      },
                    }}
                  />
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginTop: '20px' }}
                  >
                    <Grid item sm={8}>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => window.location.href = '/'}
                      >
                        <Typography variant="button" component="p">
                          {t('forgotPassword.backToSignIn')}
                        </Typography>
                      </Button>
                    </Grid>
                    <Grid item container justifyContent="flex-end" sm={4}>
                      <ButtonWithLoading
                        onClick={handleSubmit}
                        loading={loading}
                        title={t('forgotPassword.sendResetLink')}
                      />
                    </Grid>
                  </Grid>
                </form>
              )}
            </ThemeProvider>
          </Container>
        </Container>
        
        <Snackbar 
          open={openSnackbar} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity="success">
            Password reset link sent successfully!
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </>
  );
};

export default ForgotPassword;
